import classNames from "classnames"
import React, { ReactNode, ElementType, forwardRef } from "react"

import { getOrderClass } from "../../../utils/layout"

type LayoutItemProps<T extends ElementType> = {
  mobileGridOrder?: number
  as?: T
  children?: ReactNode
  className?: string
  "data-testid"?: string
} & React.ComponentPropsWithRef<T>

/**
 * Shared polymorphic component for all direct children of a layout
 *
 * Used to ensure any required attributes are
 * added to children of a layout (ie: mobile grid order)
 */
const LayoutItem = forwardRef(function LayoutItem<
  T extends ElementType = "div",
>(
  {
    mobileGridOrder,
    as,
    children,
    className,
    "data-testid": dataTestId,
    ...props
  }: LayoutItemProps<T>,
  ref: React.ForwardedRef<Element>
) {
  const Component = as || "div" // Fallback needs to explicitly be set here

  // We cant use style prop here because some components
  // take "style" as a string union (ie: PillButton).
  // so we have to use tailwind classes with
  // string interpolation for order
  // - see tailwind config for safelist
  const orderClass = mobileGridOrder && getOrderClass(mobileGridOrder)

  return (
    <Component
      ref={ref}
      data-testid={dataTestId}
      className={classNames(
        orderClass,
        {
          "sm:!order-none": mobileGridOrder,
        },
        className
      )}
      {...props}
    >
      {children}
    </Component>
  ) // This assertion is needed for prop type inference on polymorphic components
}) as <T extends ElementType = "div">(
  props: LayoutItemProps<T> & { ref?: React.ForwardedRef<HTMLElement | null> }
) => React.ReactElement | null

export default LayoutItem
